import moment, {Moment} from 'moment'

import {OptimizerRunStatus} from '../enums'
import {OptimizedSchedule} from '../interfaces/api'
import {
  OptimizerSettings,
  OptimizerSettingsDTO,
  OptimizerRun,
  OptimizerRunResponse
} from '../interfaces/api/optimizer'

const roundTo = (num: number, places: number) => {
  const factor = Math.pow(10, places)
  return Math.round(num * factor) / factor
}

const decimalPlaces = 5

export const mapOptimizerSettingsResponse = (response: OptimizerSettingsDTO): OptimizerSettings => {
  const {
    switch_cost_priority,
    silo_slack_priority,
    horizon_days,
    max_horizon_days,
    expected_durations,
    time_limit,
    optimize_from
  } = response

  if (switch_cost_priority + silo_slack_priority > 1) {
    throw new Error('BUG:Switch cost priority and silo slack priority sum cannot be greater than 1')
  }

  const currentTime = moment()
  const optimizeFrom = moment(optimize_from, 'HH:mm', true)
  if (!optimizeFrom.isValid()) {
    throw new Error('BUG:Invalid optimize from time format')
  }

  Array.from({length: max_horizon_days}).forEach((_, index) => {
    const horizonDay = index + 1
    if (!expected_durations[horizonDay]) {
      throw new Error(`BUG:Expected durations missing value for horizon day ${horizonDay}`)
    }
  })

  return {
    priorities: {
      switchCostPriority: switch_cost_priority,
      siloSlackPriority: silo_slack_priority,
      electricityCostPriority: roundTo(
        1 - switch_cost_priority - silo_slack_priority,
        decimalPlaces
      )
    },
    horizonDays: horizon_days,
    maxHorizonDays: max_horizon_days,
    expectedDurations: expected_durations,
    timeLimit: time_limit,
    optimizeFrom: currentTime.isBefore(optimizeFrom) ? optimizeFrom : optimizeFrom.add(1, 'day')
  }
}

export const mapOptimizerSettingsToDTO = (
  settings: OptimizerSettings,
  optimizeFromAsISO = false
): OptimizerSettingsDTO => {
  const {
    priorities: {switchCostPriority, siloSlackPriority},
    horizonDays,
    maxHorizonDays,
    expectedDurations,
    timeLimit,
    optimizeFrom
  } = settings

  if (switchCostPriority + siloSlackPriority > 1) {
    throw new Error('BUG:Switch cost priority and silo slack priority sum cannot be greater than 1')
  }

  if (!optimizeFrom.isValid()) {
    throw new Error('BUG:Invalid optimize from time format')
  }

  return {
    switch_cost_priority: roundTo(switchCostPriority, decimalPlaces),
    silo_slack_priority: roundTo(siloSlackPriority, decimalPlaces),
    horizon_days: horizonDays,
    max_horizon_days: maxHorizonDays,
    expected_durations: expectedDurations,
    time_limit: timeLimit,
    optimize_from: optimizeFromAsISO ? optimizeFrom.toISOString() : optimizeFrom.format('HH:mm')
  }
}

export const mapCalculatingPlansResponse = (response: OptimizerRunResponse[]): OptimizerRun[] => {
  return (
    response
      // DS BE returns all runs, even finished ones for 3 hours
      .filter((plan) => plan.status === OptimizerRunStatus.InProgress)
      .map((plan) => ({
        name: plan.name,
        forecastType: plan.forecast_type,
        status: plan.status,
        lastUpdatedAt: plan.last_updated_at,
        queuedAt: plan.queued_at,
        queuedBy: plan.queued_by,
        optimizerRunId: plan.optimizer_run_id
      }))
  )
}

/**
 * Selector which filters out schedule items before the provided date
 *
 * @param schedules array of optimized schedules
 * @param date, items that have end date before this date will be filtered out
 * @returns array of optimized schedules with filtered schedule items
 *
 */
export const selectScheduleItemsFrom = (
  schedules: OptimizedSchedule[],
  date: Moment
): OptimizedSchedule[] => {
  return schedules.map((schedule) => ({
    ...schedule,
    schedules: Object.fromEntries(
      Object.entries(schedule.schedules).filter(([, schedulItem]) =>
        moment.utc(schedulItem.end).isAfter(date.clone().utc())
      )
    )
  }))
}
